import CaplyMark from '../components/CaplyMark';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, Building2, Mail, Rocket } from 'lucide-react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';

export default function Login() {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [demoTenant, setDemoTenant] = useState('tenant-alpha-01');

  const { login, loginDemo, logout, token, profileError } = useAuth();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signup = params.get('signup') === '1';

  // Route in ONLY when the app session is fully established (MSAL account
  // AND a firm-scoped profile). Without the token check, a signed-in user
  // who isn't mapped to a firm ping-pongs between /login and /portfolio
  // until React Router gives up and renders a blank page.
  useEffect(() => {
    if (isAuthenticated && inProgress === 'none' && token) {
      navigate('/portfolio', { replace: true });
    }
  }, [isAuthenticated, inProgress, token, navigate]);

  // Older links point at /login?signup=1. A first-time visitor belongs on the
  // plan-aware sign-up page (plan, price, then one click into the hosted
  // create-account form), never on the returning-user login card.
  useEffect(() => {
    if (!signup || token || isAuthenticated) return;
    navigate('/signup?plan=deal_team&cycle=annual', { replace: true });
  }, [signup, token, isAuthenticated, navigate]);

  const handleLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await login();
    } catch (err) {
      setError(err?.message || 'Sign-in failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setIsLoading(true);
    try {
      if (loginDemo) {
        loginDemo(demoTenant);
      } else {
        login('demo@pe.com', 'demo');
      }
      navigate('/portfolio');
    } catch (err) {
      setError('Failed to initiate demo login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-brand-100 flex items-center justify-center p-4">
      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-[0.035]" style={{
        backgroundImage: 'linear-gradient(#132231 1px, transparent 1px), linear-gradient(90deg, #132231 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mb-3"><CaplyMark className="w-20 h-20 mx-auto" title="Caply" /></div>
          <h1 className="text-4xl font-extrabold text-navy-900 tracking-tight">Caply</h1>
          <p className="text-sm font-medium text-slate-500 mt-2 uppercase tracking-widest">Private Equity Intelligence Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
          {profileError === 'unassigned' && (
            <div className="mb-5 p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Account created — waiting for firm access
              </p>
              <p className="text-xs text-amber-700 leading-relaxed mt-2">
                Your Caply account is ready, but your email hasn't been invited to a
                firm yet. Ask your firm's admin to invite you from
                Settings&nbsp;→&nbsp;Team&nbsp;&amp;&nbsp;Access, then click
                &ldquo;Sign in with Email&rdquo; again.
              </p>
              <button
                onClick={() => logout()}
                className="mt-3 text-xs font-medium text-amber-700 underline hover:text-amber-900"
              >
                Sign out of this account
              </button>
            </div>
          )}
          {profileError === 'failed' && (
            <div className="mb-5 p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Signed in, but Caply couldn't load your profile. Click
                &ldquo;Sign in with Email&rdquo; to try again.
              </span>
            </div>
          )}
          {error && (
            <div className="mb-5 p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {signup && (
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-navy-900">Create your firm workspace</h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                You will be taken to the Caply sign-in page. Choose
                &ldquo;No account? Create one&rdquo; and use your work email. A new
                workspace opens immediately with sample companies so you can look around.
              </p>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoading || inProgress !== 'none'}
            className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-4 h-4" />
            {isLoading ? 'Redirecting...' : (signup ? 'Continue to sign up' : 'Sign in with Email')}
          </button>
          <p className="text-xs text-slate-400 leading-relaxed text-center mt-3">
            New firm user? Use "Sign up" on the Caply sign-in page — it is the
            same email + password experience.
          </p>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="space-y-3">
            <label className="form-label flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              Demo Firm
            </label>
            <select
              value={demoTenant}
              onChange={(e) => setDemoTenant(e.target.value)}
              className="form-input"
            >
              <option value="tenant-alpha-01">Alpha Capital Partners</option>
              <option value="tenant-beta-02">Beta Growth Equity</option>
            </select>
            <button
              onClick={handleDemoLogin}
              disabled={isLoading}
              className="btn-gold w-full py-3 disabled:opacity-50"
            >
              <Rocket className="w-4 h-4" />
              Demo Login as {demoTenant === 'tenant-beta-02' ? 'Beta Growth Equity' : 'Alpha Capital Partners'}
            </button>
            <p className="text-xs text-slate-400 leading-relaxed">
              Demo firms share the platform but see only their own portfolio data.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Private &amp; encrypted · Built for PE deal teams
        </p>
        <p className="text-center text-[11px] text-slate-400 mt-2">
          <a href="/terms.html" className="hover:text-slate-600 hover:underline">Terms of Use</a>
          <span className="mx-1.5">·</span>
          <a href="/privacy.html" className="hover:text-slate-600 hover:underline">Privacy Policy</a>
          <span className="mx-1.5">·</span>
          <a href="mailto:support@caply4pe.com" className="hover:text-slate-600 hover:underline">support@caply4pe.com</a>
        </p>
      </div>
    </div>
  );
}
